import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetRecipsInQueueQuery } from '@/app/services/inClinic';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { EmbryoPanel } from './components/EmbryoPanel';
import { ImplantToolbar } from './components/ImplantToolbar';
import { MainViewRefreshButton } from './components/MainViewRefreshButton';
import { RecipPanel } from './components/RecipPanel';
import { useImplantSession } from './hooks/useImplantSession';
import { useInClinicBasePath } from './inClinicPath';
import type { EmbryoViewMode, FreshEmbryoFilters } from './types';

export function InClinicPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useImplantSession();
  const preserveEmbryoFilters = useFeatureFlag('Implant_Preserve_Purch_Preg');
  const allowOtherLabSiteFilter = useFeatureFlag(
    'Implant_Find_From_Other_Lab_Without_Loca_Change',
  );
  const showRefresh = useFeatureFlag('In_Clinic_Refresh');
  const basePath = useInClinicBasePath();
  const [localEmbryoFilters, setLocalEmbryoFilters] =
    useState<FreshEmbryoFilters>([]);
  const [embryoSiteFilter, setEmbryoSiteFilter] = useState({
    sessionSiteId: session.siteId,
    filterSiteId: session.siteId,
  });
  const persistedEmbryoSiteId = searchParams.get('fertSiteId') || '';
  const localEmbryoSiteId =
    embryoSiteFilter.sessionSiteId === session.siteId
      ? embryoSiteFilter.filterSiteId
      : session.siteId;
  const embryoSiteId = allowOtherLabSiteFilter
    ? persistedEmbryoSiteId || localEmbryoSiteId
    : session.siteId;
  const [selectedEmbryo, setSelectedEmbryo] = useState({
    sessionSiteId: session.siteId,
    embryoSiteId,
    embryoId: null as string | null,
  });
  const selectedEmbryoId =
    selectedEmbryo.sessionSiteId === session.siteId &&
    selectedEmbryo.embryoSiteId === embryoSiteId
      ? selectedEmbryo.embryoId
      : null;
  const { data: recipsInQueue = [] } = useGetRecipsInQueueQuery(
    { yardId: session.yardId, chute: session.chuteNumber },
    { skip: !session.yardId },
  );
  const persistedEmbryoFilters = useMemo(
    () => searchParams.getAll('embryoType') as FreshEmbryoFilters,
    [searchParams],
  );
  const embryoFilters = preserveEmbryoFilters
    ? persistedEmbryoFilters
    : localEmbryoFilters;

  const viewMode: EmbryoViewMode =
    (searchParams.get('viewMode') as EmbryoViewMode) || 'fresh';

  const setViewMode = (mode: EmbryoViewMode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('viewMode', mode);
      return next;
    });
  };

  const setEmbryoSiteId = (filterSiteId: string) => {
    const nextFilterSiteId = filterSiteId || session.siteId;
    setEmbryoSiteFilter({
      sessionSiteId: session.siteId,
      filterSiteId: nextFilterSiteId,
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (
        allowOtherLabSiteFilter &&
        nextFilterSiteId &&
        nextFilterSiteId !== session.siteId
      ) {
        next.set('fertSiteId', nextFilterSiteId);
      } else {
        next.delete('fertSiteId');
      }
      return next;
    });
  };

  const setSelectedEmbryoId = (embryoId: string | null) => {
    setSelectedEmbryo({
      sessionSiteId: session.siteId,
      embryoSiteId,
      embryoId,
    });
  };

  const setEmbryoFilters = (filters: FreshEmbryoFilters) => {
    if (!preserveEmbryoFilters) {
      setLocalEmbryoFilters(filters);
      return;
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('embryoType');
      filters.forEach((filter) => next.append('embryoType', filter));
      return next;
    });
  };

  // Auto-navigate to data-entry for the top recip when an embryo is selected
  useEffect(() => {
    if (selectedEmbryoId && recipsInQueue.length > 0) {
      const topRecip = recipsInQueue[0];
      const params = new URLSearchParams({
        embryoId: selectedEmbryoId,
        recipName: topRecip.tag || topRecip.clientRecipTag || '',
        recipQueueId: topRecip.queueId.toString(),
        siteId: session.siteId,
        yardId: session.yardId,
        vetId: session.vetId,
        techId: session.techId,
        chuteNumber: session.chuteNumber.toString(),
        impDate: session.impDate,
        viewMode,
      });
      if (session.caneSearch) params.set('cane', session.caneSearch);
      if (allowOtherLabSiteFilter && embryoSiteId !== session.siteId) {
        params.set('fertSiteId', embryoSiteId);
      }
      if (preserveEmbryoFilters) {
        embryoFilters.forEach((filter) => params.append('embryoType', filter));
      }
      navigate(`${basePath}/data-entry?${params.toString()}`);
    }
  }, [
    basePath,
    selectedEmbryoId,
    recipsInQueue,
    session,
    viewMode,
    embryoSiteId,
    embryoFilters,
    allowOtherLabSiteFilter,
    preserveEmbryoFilters,
    navigate,
  ]);

  const handleRecipSelect = (recipName: string, recipQueueId: number) => {
    if (!selectedEmbryoId) return;
    const params = new URLSearchParams({
      embryoId: selectedEmbryoId,
      recipName,
      recipQueueId: recipQueueId.toString(),
      siteId: session.siteId,
      yardId: session.yardId,
      vetId: session.vetId,
      techId: session.techId,
      chuteNumber: session.chuteNumber.toString(),
      impDate: session.impDate,
      viewMode,
    });
    if (session.caneSearch) params.set('cane', session.caneSearch);
    if (allowOtherLabSiteFilter && embryoSiteId !== session.siteId) {
      params.set('fertSiteId', embryoSiteId);
    }
    if (preserveEmbryoFilters) {
      embryoFilters.forEach((filter) => params.append('embryoType', filter));
    }
    navigate(`${basePath}/data-entry?${params.toString()}`);
  };

  return (
    <div className="flex flex-col gap-3 p-4 lg:h-full lg:min-h-0 lg:overflow-hidden">
      {selectedEmbryoId && (
        <span className="self-end rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
          Embryo selected — pick a recipient to continue
        </span>
      )}

      <ImplantToolbar
        session={session}
        actions={showRefresh && <MainViewRefreshButton />}
      />

      <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:flex-row">
        <section className="ring-border flex min-w-0 flex-col overflow-hidden rounded-lg ring-1 lg:min-h-0 lg:flex-1 lg:basis-0">
          <div className="bg-muted/30 flex items-center justify-between border-b px-3 py-1.5">
            <h2 className="text-xs font-semibold tracking-wide uppercase">
              Recipients
            </h2>
          </div>
          <div className="flex flex-col p-2 lg:min-h-0 lg:flex-1">
            <RecipPanel
              yardId={session.yardId}
              chuteNumber={session.chuteNumber}
              clientRecipMode={session.clientRecipMode}
              selectedEmbryoId={selectedEmbryoId}
              onRecipSelect={handleRecipSelect}
            />
          </div>
        </section>

        <section className="ring-border flex min-w-0 flex-col overflow-hidden rounded-lg ring-1 lg:min-h-0 lg:flex-1 lg:basis-0">
          <div className="bg-muted/30 flex items-center justify-between border-b px-3 py-1.5">
            <h2 className="text-xs font-semibold tracking-wide uppercase">
              Embryos
            </h2>
          </div>
          <div className="flex flex-col p-2 lg:min-h-0 lg:flex-1">
            <EmbryoPanel
              siteId={session.siteId}
              allowSiteFilter={allowOtherLabSiteFilter}
              embryoSiteId={embryoSiteId}
              onEmbryoSiteChange={setEmbryoSiteId}
              impDate={session.impDate}
              filters={embryoFilters}
              onFiltersChange={setEmbryoFilters}
              selectedEmbryoId={selectedEmbryoId}
              onEmbryoSelect={setSelectedEmbryoId}
              queueEmpty={recipsInQueue.length === 0}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              caneSearch={session.caneSearch}
              onCaneSearchChange={session.setCaneSearch}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
